import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CancelOutlined as CancelIcon,
  CheckCircle as CheckCircleIcon,
  EventRepeat as RescheduleIcon,
  RateReview as ReviewIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  Assignment as OutcomeIcon,
} from '@mui/icons-material';
import { AppointmentResponseDto, AppointmentStatus } from '../../../types/appointment.types';
import { UserAvatar } from '../../common/UserAvatar';
import { AppointmentCountdown } from '../AppointmentCountdown';

const BASE_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]:   'Очікує підтвердження',
  [AppointmentStatus.CONFIRMED]: 'Підтверджено',
  [AppointmentStatus.CANCELLED]: 'Скасовано',
  [AppointmentStatus.COMPLETED]: 'Завершено',
};

function getClientStatusLabel(a: AppointmentResponseDto): string {
  if (a.status === AppointmentStatus.PENDING) {
    if (a.createdBy === 'expert') return 'Чека вашого підтвердження';
    if (a.createdBy === 'client') return 'Чека підтвердження експерта';
    return 'Очікує підтвердження';
  }
  if (a.status === AppointmentStatus.CANCELLED) {
    if (a.cancelledBy === 'client') return 'Скасовано вами';
    if (a.cancelledBy === 'expert') return 'Скасовано експертом';
  }
  return BASE_STATUS_LABELS[a.status];
}

const STATUS_COLORS: Record<AppointmentStatus, 'warning' | 'success' | 'error' | 'default'> = {
  [AppointmentStatus.PENDING]:   'warning',
  [AppointmentStatus.CONFIRMED]: 'success',
  [AppointmentStatus.CANCELLED]: 'error',
  [AppointmentStatus.COMPLETED]: 'default',
};

const canCancel = (s: AppointmentStatus) => s === AppointmentStatus.PENDING || s === AppointmentStatus.CONFIRMED;
// E1: reschedule дозволено ЛИШЕ для CONFIRMED. У pending зустріч ще узгоджується —
// замість контр-пропозиції часу треба confirm або cancel з причиною.
const canReschedule = (s: AppointmentStatus) => s === AppointmentStatus.CONFIRMED;
const canReview = (a: AppointmentResponseDto) => a.status === AppointmentStatus.COMPLETED && !a.review;

interface Props {
  appointments: AppointmentResponseDto[];
  loading: boolean;
  isMobile: boolean;
  onOpenDetails: (id: number) => void;
  onQuickConfirm: (id: number) => void;
  onOpenCancel: (appointment: AppointmentResponseDto) => void;
  onOpenReschedule: (appointment: AppointmentResponseDto) => void;
  onOpenReview: (appointment: AppointmentResponseDto) => void;
}

export const ClientAppointmentsTable: React.FC<Props> = ({
  appointments,
  loading,
  isMobile,
  onOpenDetails,
  onQuickConfirm,
  onOpenCancel,
  onOpenReschedule,
  onOpenReview,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (appointments.length === 0) {
    return (
      <Typography sx={{ py: 6, textAlign: 'center' }} color="text.secondary">
        Записів не знайдено.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Експерт</TableCell>
            <TableCell>Дата</TableCell>
            {!isMobile && <TableCell>Тривалість</TableCell>}
            <TableCell>Статус</TableCell>
            {!isMobile && <TableCell>Відгук</TableCell>}
            {!isMobile && <TableCell>Підсумок</TableCell>}
            <TableCell align="right">Дії</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appointments.map(a => (
            <TableRow hover key={a.id} onClick={() => onOpenDetails(a.id)} sx={{ cursor: 'pointer' }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UserAvatar path={a.expert.avatar} name={a.expert.name} sx={{ width: 36, height: 36 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.expert.name}</Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{new Date(a.scheduledAt).toLocaleDateString('uk-UA')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(a.scheduledAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <AppointmentCountdown scheduledAt={a.scheduledAt} status={a.status} />
              </TableCell>
              {!isMobile && <TableCell>{a.durationMinutes} хв.</TableCell>}
              <TableCell>
                <Chip
                  label={getClientStatusLabel(a)}
                  color={STATUS_COLORS[a.status]}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              {!isMobile && (
                <TableCell>
                  {a.review ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="body2">{a.review.rating}</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell>
                  {a.outcome ? (
                    <Tooltip title={a.outcome.length > 120 ? a.outcome.slice(0, 120) + '…' : a.outcome} arrow>
                      <OutcomeIcon fontSize="small" color="action" sx={{ cursor: 'default' }} />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              )}
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {a.status === AppointmentStatus.PENDING && a.createdBy === 'expert' && (
                    <Tooltip title="Підтвердити">
                      <IconButton size="small" color="primary" onClick={() => onQuickConfirm(a.id)}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canReschedule(a.status) && (
                    <Tooltip title="Перенести">
                      <IconButton size="small" color="primary" onClick={() => onOpenReschedule(a)}>
                        <RescheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canCancel(a.status) && (
                    <Tooltip title="Скасувати">
                      <IconButton size="small" color="error" onClick={() => onOpenCancel(a)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canReview(a) && (
                    <Tooltip title="Залишити відгук">
                      <IconButton size="small" color="warning" onClick={() => onOpenReview(a)}>
                        <ReviewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Деталі">
                    <IconButton size="small" onClick={() => onOpenDetails(a.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
